import { useCallback, useEffect, useRef, useState } from 'react'
import { Globe, X } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { useOnlineBookingInbox } from '@/hooks/useOnlineBookingInbox'
import { formatArrivalTime } from '@/lib/calendar/checkIn'
import { playOnlineBookingNotificationSound } from '@/lib/playOnlineBookingNotificationSound'
import {
  getToastSeenBookingIds,
  markToastSeenBookingIds,
} from '@/lib/onlineBookingToast'
import { hasPermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'

const AUTO_DISMISS_MS = 6000
const MAX_TOASTS = 3

/**
 * @typedef {{
 *   id: number
 *   customer_name?: string | null
 *   starts_at?: string | null
 * }} ToastItem
 */

function OnlineBookingToast({ item, onDismiss, onOpen }) {
  const { t } = useUiLocale()

  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(item.id), AUTO_DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [item.id, onDismiss])

  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      className={cn(
        'pointer-events-auto w-full max-w-sm rounded-2xl border border-border/70 bg-card/95 p-3 text-left shadow-lg backdrop-blur-md',
        'transition-transform hover:scale-[1.01] hover:shadow-xl',
        'animate-in fade-in slide-in-from-right-4 duration-300',
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700">
          <Globe className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
            {t('notifications.toastTitle')}
          </p>
          <p className="mt-0.5 text-sm font-semibold text-foreground">
            {item.customer_name || t('notifications.guestFallback')}
          </p>
          {item.starts_at ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatArrivalTime(item.starts_at)}
            </p>
          ) : null}
          <p className="mt-1 text-xs font-medium text-primary">{t('notifications.toastAction')}</p>
        </span>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onDismiss(item.id)
          }}
          className="rounded-lg p-1 text-muted-foreground hover:bg-muted/70 hover:text-foreground"
          aria-label={t('notifications.dismiss')}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </button>
  )
}

export function OnlineBookingToastHost() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { activeBranchId } = useBranch()
  const { pendingItems, markBookingSeen, loading } = useOnlineBookingInbox()
  const canManageAppointments = hasPermission(user, PERMISSIONS.APPOINTMENTS_MANAGE)
  /** @type {[ToastItem[], import('react').Dispatch<import('react').SetStateAction<ToastItem[]>>]} */
  const [toasts, setToasts] = useState([])
  const baselineReadyRef = useRef(false)
  const isPosPage = location.pathname.startsWith('/pos')

  const dismissToast = useCallback((appointmentId) => {
    setToasts((current) => current.filter((toast) => toast.id !== appointmentId))
  }, [])

  const openBooking = useCallback(
    (appointmentId) => {
      markBookingSeen(appointmentId)
      dismissToast(appointmentId)
      navigate(`/online-bookings?booking=${appointmentId}`)
    },
    [dismissToast, markBookingSeen, navigate],
  )

  useEffect(() => {
    baselineReadyRef.current = false
    setToasts([])
  }, [activeBranchId])

  useEffect(() => {
    if (!canManageAppointments || isPosPage || !activeBranchId || loading) {
      return
    }

    const pendingIds = pendingItems.map((item) => item.id)

    if (!baselineReadyRef.current) {
      markToastSeenBookingIds(activeBranchId, pendingIds)
      baselineReadyRef.current = true
      return
    }

    const toastSeenIds = getToastSeenBookingIds(activeBranchId)
    const newItems = pendingItems.filter(
      (item) => !toastSeenIds.has(item.id),
    )

    if (newItems.length === 0) {
      return
    }

    markToastSeenBookingIds(
      activeBranchId,
      newItems.map((item) => item.id),
    )
    playOnlineBookingNotificationSound()

    setToasts((current) => {
      const existingIds = new Set(current.map((toast) => toast.id))
      const merged = [
        ...newItems
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({
            id: item.id,
            customer_name: item.customer_name,
            starts_at: item.starts_at,
          })),
        ...current,
      ]

      return merged.slice(0, MAX_TOASTS)
    })
  }, [activeBranchId, canManageAppointments, isPosPage, loading, pendingItems])

  useEffect(() => {
    if (isPosPage && toasts.length > 0) {
      setToasts([])
    }
  }, [isPosPage, toasts.length])

  if (!canManageAppointments || isPosPage || toasts.length === 0) {
    return null
  }

  return (
    <div className="pointer-events-none fixed bottom-5 right-4 z-[60] flex w-[min(100vw-2rem,22rem)] flex-col gap-2 sm:right-6">
      {toasts.map((item) => (
        <OnlineBookingToast
          key={item.id}
          item={item}
          onDismiss={dismissToast}
          onOpen={openBooking}
        />
      ))}
    </div>
  )
}
